// Password Protection Modal - Set password when uploading sensitive documents
import React, { useState } from 'react';
import { Lock, Eye, EyeOff, X } from 'lucide-react';

interface PasswordProtectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (password: string) => void;
  fileName: string;
}

export default function PasswordProtectionModal({
  isOpen,
  onClose,
  onConfirm,
  fileName
}: PasswordProtectionModalProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!password) {
      setError('Password is required');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    onConfirm(password);
    setPassword('');
    setConfirmPassword('');
    setError('');
  };

  const handleClose = () => {
    setPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-amber-500/30 rounded-2xl p-6 max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Lock className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Password Protect</h3>
              <p className="text-xs text-slate-400">Encrypt this document</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* File Name */}
        <div className="mb-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
          <p className="text-sm text-slate-300 truncate">{fileName}</p>
        </div>

        {/* Password Input */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-slate-300 mb-2">
            Enter Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              placeholder="Minimum 6 characters"
              className="w-full px-4 py-3 pr-12 bg-slate-800 border border-slate-600 rounded-lg 
                       text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 
                       transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Confirm Password Input */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-slate-300 mb-2">
            Confirm Password
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setError('');
            }}
            placeholder="Re-enter password"
            className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg 
                     text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 
                     transition-colors"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Info */}
        <div className="mb-6 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-xs text-blue-300">
            🔒 Your document will be encrypted before upload. You'll need this password to view it later.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-3 rounded-lg border border-slate-600 hover:bg-slate-800 
                     text-white font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 
                     hover:from-amber-600 hover:to-orange-700 text-white font-semibold 
                     transition-all shadow-lg shadow-amber-500/30"
          >
            Encrypt & Upload
          </button>
        </div>
      </div>
    </div>
  );
}
